import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { PortalPageHead } from '@/components/portal/PortalChrome';
import { PortalIcon } from '@/components/portal/PortalIcon';
import { formatPortalDateTime } from '@/lib/portalFormat';
import { usePortalActiveClinic } from '@/lib/portalActiveClinic';

interface DataExportRequest {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'expired';
  format: 'json' | 'pdf' | 'zip';
  created_at: string;
  download_url: string | null;
  url_expires_at: string | null;
  data_summary: {
    appointments_count?: number;
    invoices_count?: number;
    questionnaires_count?: number;
    journal_entries_count?: number;
    documents_count?: number;
    sms_logs_count?: number;
    quotes_count?: number;
    lab_reports_count?: number;
    lab_reports_withheld_refused_consent?: number;
  } | null;
  error_message: string | null;
}

export function PortalDataExport() {
  const queryClient = useQueryClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { activePatientId, activeClinicId, clinics, isLoading: clinicsLoading } = usePortalActiveClinic();
  const clinicName = clinics.find((clinic) => clinic.clinic_id === activeClinicId)?.clinic_name ?? null;

  const { data: exportRequests, isLoading: requestsLoading } = useQuery({
    queryKey: ['data-export-requests', activePatientId ?? null],
    queryFn: async () => {
      if (!activePatientId) return [];

      const { data, error: listError } = await supabase
        .from('data_export_requests')
        .select('*')
        .eq('patient_id', activePatientId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (listError) throw listError;
      return data as DataExportRequest[];
    },
    enabled: Boolean(activePatientId),
  });
  const isLoading = clinicsLoading || requestsLoading;

  const requestExportMutation = useMutation({
    mutationFn: async () => {
      if (!activePatientId) throw new Error('Patient ID not found');

      const { data, error: rpcError } = await supabase.rpc('request_data_export', {
        p_patient_id: activePatientId,
        p_format: 'zip',
      });

      if (rpcError) throw rpcError;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['data-export-requests', activePatientId] });
      setShowConfirmModal(false);
      setError(null);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Eksporten kunne ikke bestilles.');
    },
  });

  return (
    <>
      <PortalPageHead
        title="Mine data"
        subtitle={
          clinicName
            ? `Bestil en kopi af dine data hos ${clinicName}. Linket gælder i 7 dage.`
            : 'Bestil en kopi af dine data. Linket gælder i 7 dage.'
        }
      />

      {error ? (
        <p className="help" style={{ color: 'var(--red-ink)', marginBottom: 16 }}>
          {error}
        </p>
      ) : null}

      <div className="notice teal" style={{ marginBottom: 16 }}>
        <PortalIcon name="info" size="sm" />
        <span>
          Eksporten indeholder personlige oplysninger, aftaler, fakturaer, spørgeskemaer,
          journalnotater, kommunikation og laboratoriesvar, som du har givet samtykke til
          at dele. Svar med nægtet samtykke indgår ikke.{' '}
          <button type="button" className="link" onClick={() => setShowInfoModal(true)}>
            Læs mere om dine rettigheder
          </button>
        </span>
      </div>

      <section className="card">
        <div className="card-h">
          <h2>Tidligere anmodninger</h2>
          <div className="r">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowConfirmModal(true)}
              disabled={requestExportMutation.isPending || !activePatientId}
            >
              <PortalIcon name="download" size="sm" />
              Bestil eksport
            </button>
          </div>
        </div>

        {isLoading ? (
          <p className="help">Henter anmodninger…</p>
        ) : !activePatientId ? (
          <p className="help">Vælg en klinik under Klinikker, før du bestiller en eksport.</p>
        ) : !exportRequests || exportRequests.length === 0 ? (
          <p className="help">Du har ikke bestilt en dataeksport endnu.</p>
        ) : (
          exportRequests.map((request) => {
            const expired = Boolean(request.url_expires_at && isExpired(request.url_expires_at));
            const status = expired ? 'expired' : request.status;
            const chip = statusChip(status);
            const canDownload =
              request.status === 'completed' && Boolean(request.download_url) && !expired;

            return (
              <div key={request.id} className="row">
                <span className="ic-tile">
                  <PortalIcon name="folder" />
                </span>
                <div className="grow">
                  <div className="t">{chip.label}</div>
                  <div className="s">
                    Bestilt {formatPortalDateTime(request.created_at)}
                    {request.url_expires_at && !expired && request.status === 'completed'
                      ? ` · udløber ${formatPortalDateTime(request.url_expires_at)}`
                      : ''}
                  </div>
                  {request.data_summary && request.status === 'completed' ? (
                    <div className="s">{summaryLine(request.data_summary)}</div>
                  ) : null}
                  {request.error_message ? (
                    <div className="s">{request.error_message}</div>
                  ) : null}
                  {expired ? (
                    <div className="s">Downloadlinket er udløbet. Bestil en ny eksport.</div>
                  ) : null}
                </div>
                <div className="r">
                  <span className={`chip chip-${chip.tone}`}>
                    <PortalIcon name={chip.icon} size="xs" />
                    {chip.label}
                  </span>
                  {canDownload ? (
                    <a
                      className="btn btn-secondary"
                      href={request.download_url ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <PortalIcon name="download" size="sm" />
                      Download
                    </a>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </section>

      {showConfirmModal ? (
        <>
          <div className="scrim" onClick={() => !requestExportMutation.isPending && setShowConfirmModal(false)} />
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="export-title">
            <div className="grab" />
            <div className="mh">
              <div>
                <h2 id="export-title">Bestil dataeksport</h2>
                <div className="help">
                  {clinicName
                    ? `Vi samler dine data hos ${clinicName} og sender et downloadlink. Det gælder i 7 dage.`
                    : 'Vi samler dine data og sender et downloadlink. Det gælder i 7 dage.'}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-tertiary btn-icon x"
                aria-label="Luk"
                onClick={() => !requestExportMutation.isPending && setShowConfirmModal(false)}
              >
                <PortalIcon name="x" />
              </button>
            </div>
            <div className="mb">
              <p className="help">
                Behandlingstiden er typisk under et døgn. GDPR giver os op til 30 dage.
              </p>
            </div>
            <div className="mf">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowConfirmModal(false)}
                disabled={requestExportMutation.isPending}
              >
                Annuller
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => requestExportMutation.mutate()}
                disabled={requestExportMutation.isPending || !activePatientId}
              >
                {requestExportMutation.isPending ? 'Sender anmodning…' : 'Bestil eksport'}
              </button>
            </div>
          </div>
        </>
      ) : null}

      {showInfoModal ? (
        <>
          <div className="scrim" onClick={() => setShowInfoModal(false)} />
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="gdpr-title">
            <div className="grab" />
            <div className="mh">
              <div>
                <h2 id="gdpr-title">Dine GDPR-rettigheder</h2>
                <div className="help">Ret til indsigt (GDPR artikel 15)</div>
              </div>
              <button
                type="button"
                className="btn btn-tertiary btn-icon x"
                aria-label="Luk"
                onClick={() => setShowInfoModal(false)}
              >
                <PortalIcon name="x" />
              </button>
            </div>
            <div className="mb">
              <p className="help" style={{ marginBottom: 12 }}>
                Du har ret til at få bekræftet, om vi behandler personoplysninger om dig, og i givet
                fald få adgang til dem.
              </p>
              <p className="help" style={{ marginBottom: 12 }}>
                Dataene leveres i maskinlæsbart format (JSON) i en ZIP-fil. Downloadlinket udløber
                efter 7 dage.
              </p>
              <p className="help" style={{ marginBottom: 12 }}>
                Laboratoriesvar, hvor der er angivet nægtet samtykke til deling, indgår ikke i
                eksporten og vises ikke i portalen.
              </p>
              <p className="help">
                Vi behandler din anmodning inden for 30 dage. De fleste anmodninger er klar inden
                for 24 timer.
              </p>
            </div>
            <div className="mf">
              <button type="button" className="btn btn-primary" onClick={() => setShowInfoModal(false)}>
                Forstået
              </button>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}

function isExpired(expiresAt: string): boolean {
  return new Date(expiresAt) < new Date();
}

function statusChip(status: string): {
  tone: 'teal' | 'green' | 'amber' | 'neutral' | 'red';
  icon: 'check' | 'clock' | 'x' | 'info';
  label: string;
} {
  switch (status) {
    case 'completed':
      return { tone: 'green', icon: 'check', label: 'Klar til download' };
    case 'failed':
      return { tone: 'red', icon: 'x', label: 'Fejlet' };
    case 'processing':
      return { tone: 'teal', icon: 'clock', label: 'Behandler' };
    case 'pending':
      return { tone: 'amber', icon: 'clock', label: 'Afventer' };
    case 'expired':
      return { tone: 'neutral', icon: 'info', label: 'Udløbet' };
    default:
      return { tone: 'neutral', icon: 'info', label: status };
  }
}

function summaryLine(summary: NonNullable<DataExportRequest['data_summary']>): string {
  const parts = [
    summary.appointments_count ? `${summary.appointments_count} aftaler` : null,
    summary.invoices_count ? `${summary.invoices_count} fakturaer` : null,
    summary.questionnaires_count ? `${summary.questionnaires_count} spørgeskemaer` : null,
    summary.journal_entries_count ? `${summary.journal_entries_count} journalnotater` : null,
    summary.documents_count ? `${summary.documents_count} dokumenter` : null,
    summary.sms_logs_count ? `${summary.sms_logs_count} SMS` : null,
    summary.quotes_count ? `${summary.quotes_count} tilbud` : null,
    summary.lab_reports_count ? `${summary.lab_reports_count} laboratoriesvar` : null,
  ].filter(Boolean);
  return parts.length > 0 ? `Inkluderer ${parts.join(', ')}` : '';
}
